import React, { useEffect, useMemo, useState } from 'react';
import { Route, Link, Redirect, useHistory } from 'react-router-dom';
import { MdOutlineArrowBack } from "@react-icons/all-files/md/MdOutlineArrowBack";
import { LoadingView, HtmlHeader } from 'day8-react';

import { cancelBooking, getActiveBooking } from '../../actions/booking';
import Summary from '../../components/booking/Summary';
import Payment from './Payment';
import { BOOKING, COMPANIES } from '../../constants';
import Spacer from '../../components/Spacer';
import { Divider, Button, CurrencyContext } from 'day8-ui';
import { isCabinBooking } from '../../helpers/booking';
import { getCabinsTypesQueryFromBooking } from '../../helpers/week';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useAutomaticDiscounts } from '../../context/AutomaticDiscountsContext';

const YachtBooking = ({ match }) => {
  const history = useHistory();
  const [redirecting, setRedirecting] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const { discountData } = useAutomaticDiscounts();
  const dispatch = useDispatch();
  const booking = useSelector(state => state.booking.currentBooking);
  const isYachtsAndFriends = useMemo(() => {
    return +booking?.week?.company?.id === +COMPANIES.YNF;
  }, [booking]);
  const user = useSelector(state => state.user.loggedUser);
  const selectedDestination = useSelector(state => state.destinations.selected);
  // if no booking, fetch active booking
  useEffect(() => {
    if (!booking || !booking.id) {
      const fetchActiveBooking = async () => {
        const response = await dispatch(getActiveBooking());
        if (!response) {
          history.push(`/browse${isYachtsAndFriends ? '/yachtsandfriends' : ''}`);
        }
      };
      fetchActiveBooking();
    }
  }, [booking, dispatch, isYachtsAndFriends]);

  // Periodically check if discount is applied
  useEffect(() => {
    if (booking) {
      const bookingCreatedAt = new Date(booking.bookingDate);
      const interval = setInterval(() => {
        const now = new Date();
        const elapsedTimeFromBooking = Math.floor((now - bookingCreatedAt) / 1000);
        if (
          elapsedTimeFromBooking >= 30 ||
          booking.hasPromoCodeApplied ||
          booking.status !== BOOKING.PENDING_STATUS ||
          (discountData && !(discountData?.[booking.week.id]?.discounts?.length > 0))
        ) {
          setIsRefetching(false);
          clearInterval(interval); // Stop checking once conditions are met
        } else {
          setIsRefetching(true);
          dispatch(getActiveBooking()); // Re-fetch booking to see if discount has been applied
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval); // Clear interval on unmount
    }
  }, [booking, dispatch]);
  const backLink = useMemo(() => {
    if (booking) {
      let capacityQuery = `?boys=${booking?.numBoys}&girls=${booking?.numGirls}`;
      if (booking.boatWeek && booking.bookingType === BOOKING.NORMAL_TYPE) {
        return `/browse/${isYachtsAndFriends ? 'yachtsandfriends/charter/' : ''}yachts/${booking.boatWeek.id}${capacityQuery}`;
      } else {
        capacityQuery = isCabinBooking(booking) ? getCabinsTypesQueryFromBooking(booking) : capacityQuery;
        return `/browse${isYachtsAndFriends ? "/yachtsandfriends" : ''}${isCabinBooking(booking) ? "/cabins" : ''}/confirm?weeks=${booking.week.week}&year=${booking.week.year}&dest=${booking.week.destination?.id}&category=${booking?.package?.id}${capacityQuery}`;
      }
    }
    return "/browse";
  }, [booking, isYachtsAndFriends]);
  if (!booking || !booking.id) {
    return <LoadingView />;
  }
  if (!user) {
    return <Redirect to={backLink} />;
  }
  // check if should redirect between YNF/TYW payment pages
  // hard refresh is required to fetch all necesary destiantions and yachts for required company
  // when switching between YNF and TYW
  const isYachtsAndFriendsRoute = /\/booking\/yachtsandfriends\//.test(window.location.href);
  if (isYachtsAndFriends && !isYachtsAndFriendsRoute) {
    setRedirecting(true);
    window.location.href = `/booking/yachtsandfriends/payment`;
    return <LoadingView title="Redirecting..." />;
  }
  if (!isYachtsAndFriends && isYachtsAndFriendsRoute) {
    setRedirecting(true);
    window.location.href = `/booking/payment`;
    return <LoadingView title="Redirecting..." />;
  }
  return (
    <div>
      {redirecting && <LoadingView title="Redirecting..." />}
      <div className='dui-container dui-mx-auto'>
        <HtmlHeader title={`Checkout Booking | ${isYachtsAndFriends ? 'Yachts & Friends' : 'Yacht Week'}`} />
        <Spacer />
        <Link to={backLink}>
          <Button leftIcon={<MdOutlineArrowBack />} buttonType={"LINK"}>Back</Button>
        </Link>
        <Spacer />
        <Divider />
        <Spacer />
        <div className="dui-w-full dui-flex dui-flex-col lg:dui-flex-row dui-gap-8 lg:dui-gap-24">
          <div className='dui-w-full lg:dui-w-3/5'>
            <Route path={`${match.url}/payment`} render={routeProps => <Payment {...routeProps} isRefetchingBooking={isRefetching} isYachtsAndFriends={isYachtsAndFriends} companyId={!!isYachtsAndFriends && COMPANIES.YNF} />} />
            <Route path={`${match.url}/cancel`} render={() => {
              if (booking?.id) {
                dispatch(cancelBooking(booking.id));
              }
              return <Redirect to={`/browse${isYachtsAndFriends ? '/yachtsandfriends' : ''}`} />;
            }} />
          </div>
          <div className='dui-w-full lg:dui-w-2/5'>
            <CurrencyContext.Consumer>
              {context => (
                <Summary
                  isYachtsAndFriends={isYachtsAndFriends}
                  user={user}
                  booking={booking}
                  location={history.location}
                  match={match}
                  destinationImg={selectedDestination && selectedDestination.img}
                  currency={context.currency}
                />
              )}
            </CurrencyContext.Consumer>
          </div>
        </div>
      </div>
    </div>
  );
};
export default YachtBooking;
